import Dexie, { type EntityTable } from 'dexie';
import type {
  CanonicalTrainingEvent,
  TrainingAttempt,
  CompetencyMastery,
  LearnerProfile,
  SyncQueueItem,
  EventSyncRecord,
  DeviceMetadata,
  UserSettings,
  DiagnosticLogEntry,
  TrainingSessionRecord,
} from './models';

export class CallFoundryDexie extends Dexie {
  events!: EntityTable<CanonicalTrainingEvent, 'eventId'>;
  attempts!: EntityTable<TrainingAttempt, 'id'>;
  masteryProfiles!: EntityTable<CompetencyMastery, 'skillId'>;
  learnerProfiles!: EntityTable<LearnerProfile, 'userId'>;
  syncQueue!: EntityTable<SyncQueueItem, 'id'>;
  syncMetadata!: EntityTable<EventSyncRecord, 'eventId'>;
  deviceMetadata!: EntityTable<DeviceMetadata, 'key'>;
  userSettings!: EntityTable<UserSettings, 'id'>;
  diagnostics!: EntityTable<DiagnosticLogEntry, 'id'>;
  trainingSessions!: EntityTable<TrainingSessionRecord, 'sessionId'>;

  constructor(dbName = 'callfoundry_db') {
    super(dbName);

    // Version 1: Initial schema foundation
    this.version(1).stores({
      events: 'eventId, userId, deviceId, eventType, skillId, clientCreatedAt, syncState, [deviceId+deviceSequence]',
      attempts: 'id, drillId, skillId, completedAt, synced',
      masteryProfiles: 'skillId, pillar, masteryState, evidenceFreshness',
      learnerProfiles: 'userId, activeJobProfile',
      syncQueue: 'id, eventId, userId, status, createdAt',
      userSettings: 'id, userId',
      diagnostics: 'id, timestamp, level, context',
    });

    // Version 2: Added score indices
    this.version(2)
      .stores({
        events: 'eventId, userId, deviceId, eventType, skillId, clientCreatedAt, syncState, [deviceId+deviceSequence]',
        attempts: 'id, drillId, skillId, score, completedAt, synced',
        masteryProfiles: 'skillId, pillar, masteryState, evidenceFreshness, score',
        learnerProfiles: 'userId, activeJobProfile',
        syncQueue: 'id, eventId, userId, status, createdAt',
        userSettings: 'id, userId',
        diagnostics: 'id, timestamp, level, context',
      })
      .upgrade((tx) => {
        return tx.table('userSettings').toCollection().modify((settings: UserSettings) => {
          if (settings.theme === undefined) {
            settings.theme = 'dark';
          }
        });
      });

    // Version 3: Atomic device sequence, transport isolation, originLearnerId
    this.version(3)
      .stores({
        events: 'eventId, originLearnerId, userId, deviceId, eventType, skillId, clientCreatedAt, [deviceId+deviceSequence]',
        attempts: 'id, drillId, skillId, score, completedAt, synced',
        masteryProfiles: 'skillId, pillar, masteryState, evidenceFreshness, score',
        learnerProfiles: 'userId, activeJobProfile',
        syncQueue: 'id, eventId, userId, accountUid, status, createdAt',
        syncMetadata: 'eventId, status, syncedAt, accountUid',
        deviceMetadata: 'key',
        userSettings: 'id, userId',
        diagnostics: 'id, timestamp, level, context',
      })
      .upgrade(async (tx) => {
        // Upgrade existing events: populate originLearnerId from userId
        await tx.table('events').toCollection().modify((event: Record<string, unknown>) => {
          if (!event.originLearnerId) {
            event.originLearnerId = event.userId || 'loc_unknown';
          }
        });
      });

    // Version 4: Complete separation of canonical event from account ownership
    this.version(4)
      .stores({
        events: 'eventId, originLearnerId, deviceId, eventType, skillId, clientCreatedAt, [deviceId+deviceSequence]',
        attempts: 'id, drillId, skillId, score, completedAt, synced',
        masteryProfiles: 'skillId, pillar, masteryState, evidenceFreshness, score',
        learnerProfiles: 'userId, activeJobProfile',
        syncQueue: 'id, eventId, accountUid, status, createdAt',
        syncMetadata: 'eventId, status, localAckAt, serverReceivedAt, accountUid',
        deviceMetadata: 'key',
        userSettings: 'id, userId',
        diagnostics: 'id, timestamp, level, context',
      })
      .upgrade(async (tx) => {
        // Upgrade existing events: strip transport userId field from canonical events
        await tx.table('events').toCollection().modify((event: Record<string, unknown>) => {
          if ('userId' in event) {
            delete event.userId;
          }
        });
      });

    // Version 5: Persistent training session runner and recovery
    this.version(5).stores({
      events: 'eventId, originLearnerId, deviceId, eventType, skillId, clientCreatedAt, [deviceId+deviceSequence]',
      attempts: 'id, drillId, skillId, score, completedAt, synced',
      masteryProfiles: 'skillId, pillar, masteryState, evidenceFreshness, score',
      learnerProfiles: 'userId, activeJobProfile',
      syncQueue: 'id, eventId, accountUid, status, createdAt',
      syncMetadata: 'eventId, status, localAckAt, serverReceivedAt, accountUid',
      deviceMetadata: 'key',
      userSettings: 'id, userId',
      diagnostics: 'id, timestamp, level, context',
      trainingSessions: 'sessionId, completionState, startedAt, lastUpdatedAt',
    });
  }
}

// Canonical singleton database instance for client runtime
export const db = new CallFoundryDexie();
